import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import ActivityAvatar from '../components/ActivityAvatar';
import SupportInbox from './SupportInbox';
import AppShell, { useShellUser } from '../components/AppShell';
import visaLogo from '../assets/images/visa-logo.png';
import mastercardLogo from '../assets/images/mastercard-logo.png';
import { ReactComponent as LockIcon } from '../assets/icons/lock-filled.svg';
import { ReactComponent as ShowPassIcon } from '../assets/icons/show_pass.svg';
import { ReactComponent as HidePassIcon } from '../assets/icons/hide_pass.svg';
import '../styles/Settings.css';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

const RESEND_COOLDOWN_SECONDS = 60;
const VERIFICATION_EMAIL_STORAGE_KEY = 'last_verification_email_sent';

const TABS = [
  { id: 'personal', label: 'Personal info' },
  { id: 'security', label: 'Security' },
  { id: 'cards', label: 'Linked cards' },
  { id: 'activity', label: 'Activity log' },
  { id: 'support', label: 'Help & support' },
];

function errorText(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    for (const key of ['error', 'detail', 'details', 'message']) {
      if (typeof data[key] === 'string' && data[key].trim()) return data[key];
    }
    const first = Object.values(data).flat().find((value) => typeof value === 'string' && value.trim());
    if (first) return first;
  }
  return error?.message || fallback;
}

function formatPhoneNumber(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function passwordProblem(value) {
  if (value.length < 8) return 'Password must be at least 8 characters long';
  if (value.length > 128) return 'Password must be at most 128 characters long';
  if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(value)) return 'Password must contain at least one number';
  return '';
}

function isEarlyRelease(user) {
  const school = user?.active_school || user?.activeSchool;
  return school?.earlyRelease === true || school?.early_release === true;
}

function Notice({ tone, children }) {
  return <p className={`settings-notice is-${tone}`}>{children}</p>;
}

function SectionHead({ title, subtitle, action }) {
  return (
    <div className="settings-head">
      <div>
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="settings-field">
      <span className="settings-label">{label}</span>
      {children}
      {hint ? <span className="settings-hint">{hint}</span> : null}
    </label>
  );
}

function PasswordInput({ value, onChange, placeholder, autoComplete }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="settings-input-wrap">
      <input
        className="settings-input"
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        className="settings-eye"
        aria-label={visible ? 'Hide password' : 'Show password'}
        onClick={() => setVisible((open) => !open)}
      >
        {visible ? <HidePassIcon /> : <ShowPassIcon />}
      </button>
    </span>
  );
}

function PersonalInfo() {
  const { setUser } = useShellUser();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    city: '',
    state: '',
    phoneNumber: '',
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiService
      .fetchCurrentUser()
      .then((user) => {
        if (cancelled || !user) return;
        setForm({
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          email: user.email || '',
          city: user.city || '',
          state: user.state || '',
          phoneNumber: formatPhoneNumber(user.phone_number || ''),
        });
      })
      .catch(() => {
        if (!cancelled) setMessage({ tone: 'error', text: 'Failed to load profile information' });
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const rawPhone = form.phoneNumber.replace(/\D/g, '');
      const user = await apiService.updateUserProfile({
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        city: form.city || null,
        state: form.state || null,
        phone_number: rawPhone || null,
      });
      if (user) setUser(user);
      setMessage({ tone: 'success', text: 'Profile updated successfully' });
    } catch (error) {
      setMessage({ tone: 'error', text: errorText(error, 'Failed to update profile. Please try again.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="settings-section" onSubmit={save}>
      <SectionHead title="Personal info" subtitle="Update your name, location and phone number." />
      <div className="settings-grid">
        <Field label="First name">
          <input
            className="settings-input"
            value={form.firstName}
            onChange={(event) => update('firstName', event.target.value)}
            placeholder="Enter your first name"
            autoComplete="given-name"
            disabled={!loaded}
          />
        </Field>
        <Field label="Last name">
          <input
            className="settings-input"
            value={form.lastName}
            onChange={(event) => update('lastName', event.target.value)}
            placeholder="Enter your last name"
            autoComplete="family-name"
            disabled={!loaded}
          />
        </Field>
      </div>
      <Field label="Email" hint="Contact support to update your email">
        <input className="settings-input" value={form.email} readOnly disabled />
      </Field>
      <div className="settings-grid">
        <Field label="City">
          <input
            className="settings-input"
            value={form.city}
            onChange={(event) => update('city', event.target.value)}
            placeholder="Enter your city"
            autoComplete="address-level2"
            disabled={!loaded}
          />
        </Field>
        <Field label="State">
          <select
            className="settings-input"
            value={form.state}
            onChange={(event) => update('state', event.target.value)}
            disabled={!loaded}
          >
            <option value="">Select your state</option>
            {form.state && !US_STATES.includes(form.state) ? (
              <option value={form.state}>{form.state}</option>
            ) : null}
            {US_STATES.map((state) => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Phone number">
        <input
          className="settings-input"
          value={form.phoneNumber}
          onChange={(event) => update('phoneNumber', formatPhoneNumber(event.target.value))}
          placeholder="(123) 456-7890"
          inputMode="tel"
          autoComplete="tel"
          maxLength={14}
          disabled={!loaded}
        />
      </Field>
      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
      <div className="settings-actions">
        <button type="submit" className="settings-btn is-primary" disabled={saving || !loaded}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

function VerifyEmail({ user }) {
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    const tick = () => {
      const last = Number(localStorage.getItem(VERIFICATION_EMAIL_STORAGE_KEY) || 0);
      const remaining = last ? RESEND_COOLDOWN_SECONDS - Math.floor((Date.now() - last) / 1000) : 0;
      setCooldown(remaining > 0 ? remaining : 0);
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  if (!user) return null;

  if (user.email_verified) {
    return (
      <div className="settings-verify is-verified">
        <strong>Email verified</strong>
        <span>{user.email}</span>
      </div>
    );
  }

  const resend = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    setError('');
    try {
      const response = await apiService.resendVerificationEmail();
      if (response?.success) {
        localStorage.setItem(VERIFICATION_EMAIL_STORAGE_KEY, String(Date.now()));
        setCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err) {
      setError(errorText(err, 'Failed to send verification email. Please try again later.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="settings-verify">
      <span className="settings-verify-mark" aria-hidden="true">!</span>
      <span className="settings-verify-copy">
        <strong>Verify your email</strong>
        <span>Please verify your email address to get full access to all app features</span>
        {error ? <span className="settings-verify-error">{error}</span> : null}
      </span>
      <button type="button" className="settings-btn is-primary" onClick={resend} disabled={sending || cooldown > 0}>
        {sending ? 'Sending...' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Send verification email'}
      </button>
    </div>
  );
}

function Security() {
  const navigate = useNavigate();
  const { user, setUser } = useShellUser();
  const hasPassword = user?.has_password !== false;
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    let problem = '';
    if (hasPassword && !form.current) problem = 'Current password is required';
    if (!problem) problem = passwordProblem(form.next);
    if (!problem && !form.confirm) problem = 'Please confirm your new password';
    if (!problem && form.next !== form.confirm) problem = 'Passwords do not match';
    if (!problem && hasPassword && form.current === form.next) {
      problem = 'New password must be different from current password';
    }
    if (problem) {
      setMessage({ tone: 'error', text: problem });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const data = await apiService.changePassword({
        current_password: hasPassword ? form.current : undefined,
        new_password: form.next,
      });
      if (data?.user) setUser(data.user);
      else {
        const fresh = await apiService.fetchCurrentUser();
        if (fresh) setUser(fresh);
      }
      setForm({ current: '', next: '', confirm: '' });
      setMessage({ tone: 'success', text: 'Password changed successfully' });
    } catch (error) {
      setMessage({ tone: 'error', text: errorText(error, 'Failed to change password. Please try again.') });
    } finally {
      setSaving(false);
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setDeletePassword('');
    setDeleteError('');
  };

  const confirmDelete = async () => {
    const password = deletePassword.trim();
    if (!password) {
      setDeleteError('Enter your password to delete your account.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await apiService.deleteAccount(password);
      navigate('/login', { replace: true });
    } catch (error) {
      setDeleteError(
        error?.response?.data?.password?.[0] ||
          errorText(error, 'Failed to delete account. Please try again.')
      );
      setDeleting(false);
    }
  };

  return (
    <div className="settings-section">
      <SectionHead title="Security" subtitle="Manage your password and account." />
      <VerifyEmail user={user} />
      <form className="settings-card" onSubmit={submit}>
        <h3>{hasPassword ? 'Change password' : 'Set password'}</h3>
        {!hasPassword ? (
          <Notice tone="warning">
            Your account doesn&apos;t have a password yet (you signed in with Google/Apple).
            Set a password so you can sign in later using your email and password.
          </Notice>
        ) : null}
        {hasPassword ? (
          <Field label="Current password">
            <PasswordInput
              value={form.current}
              onChange={(value) => update('current', value)}
              placeholder="Enter your current password"
              autoComplete="current-password"
            />
          </Field>
        ) : null}
        <Field label="New password">
          <PasswordInput
            value={form.next}
            onChange={(value) => update('next', value)}
            placeholder="Enter your new password"
            autoComplete="new-password"
          />
        </Field>
        <Field label="Confirm new password">
          <PasswordInput
            value={form.confirm}
            onChange={(value) => update('confirm', value)}
            placeholder="Confirm your new password"
            autoComplete="new-password"
          />
        </Field>
        {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
        <div className="settings-actions">
          <button type="submit" className="settings-btn is-primary" disabled={saving}>
            {saving ? 'Saving...' : hasPassword ? 'Change password' : 'Set password'}
          </button>
        </div>
      </form>
      <div className="settings-card settings-danger">
        <span>
          <h3>Delete account</h3>
          <p>Your account and all your points will be deleted permanently.</p>
        </span>
        <button type="button" className="settings-btn is-danger" onClick={() => setDeleteOpen(true)}>
          Delete account
        </button>
      </div>
      {deleteOpen ? (
        <div className="settings-modal" role="presentation" onClick={closeDelete}>
          <div className="settings-dialog" role="dialog" aria-modal="true" aria-label="Delete account" onClick={(event) => event.stopPropagation()}>
            <h3>Delete account</h3>
            <p>Your account and all your points will be deleted permanently. This cannot be undone.</p>
            <Field label="Enter your password to confirm">
              <PasswordInput
                value={deletePassword}
                onChange={setDeletePassword}
                placeholder="Password"
                autoComplete="current-password"
              />
            </Field>
            {deleteError ? <Notice tone="error">{deleteError}</Notice> : null}
            <div className="settings-dialog-actions">
              <button type="button" className="settings-btn" onClick={closeDelete} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="settings-btn is-danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function cardBrand(card) {
  return String(card?.network || card?.brand || card?.scheme || '').trim().toLowerCase();
}

function cardLogo(brand) {
  if (brand.includes('visa')) return visaLogo;
  if (brand.includes('master') || brand === 'mc') return mastercardLogo;
  return null;
}

function cardLabel(card) {
  const brand = cardBrand(card);
  let name = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';
  if (brand === 'mc' || brand.startsWith('master')) name = 'Mastercard';
  return `${name} •••• ${String(card?.last4 || card?.last_four || '')}`;
}

function formatCardNumber(value) {
  return String(value || '').replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ').slice(0, 19);
}

function formatExpiry(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 4);
  let month = digits.slice(0, 2);
  if (month.length === 2) {
    const num = Number(month);
    if (num > 12) month = '12';
    if (num === 0) month = '01';
  }
  const year = digits.slice(2, 4);
  return month.length === 2 && (year || digits.length > 2) ? `${month} / ${year}` : month;
}

function formatZip(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 9);
  return digits.length <= 5 ? digits : `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

function AddCardDialog({ onClose, onLinked }) {
  const [form, setForm] = useState({ name: '', number: '', expiry: '', cvv: '', zip: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [earned, setEarned] = useState(null);
  const [done, setDone] = useState(false);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    const digits = form.number.replace(/\s/g, '');
    const [month = '', year = ''] = form.expiry.split('/').map((part) => part.trim());
    if (digits.length < 13 || digits.length > 19) return 'Please enter a valid card number';
    if (month.length !== 2 || year.length !== 2) return 'Please enter the expiry date';
    if (form.cvv.length < 3 || form.cvv.length > 4) return 'Please enter a valid CVV code';
    if (!form.name.trim()) return 'Please enter the cardholder name';
    if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) return 'Please enter a valid zip code';
    const now = new Date();
    const yy = now.getFullYear() % 100;
    if (Number(year) < yy || (Number(year) === yy && Number(month) < now.getMonth() + 1)) {
      return 'This card has expired';
    }
    return '';
  };

  const submit = async (event) => {
    event.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError('');
    try {
      let alreadyHadCard = false;
      try {
        const existing = await apiService.getPaymentCards();
        const list = existing?.results || existing?.data || existing || [];
        alreadyHadCard = Array.isArray(list) && list.some((card) => (card?.status || 'active') === 'active');
      } catch (_error) {
        alreadyHadCard = false;
      }
      try {
        await apiService.createOliveMember();
      } catch (err) {
        const msg = String(err?.message || '');
        if (!(err?.response?.data?.existing_member || err?.response?.status === 409 || msg.includes('already exists'))) {
          throw new Error(`Failed to create or verify Olive member: ${errorText(err, msg)}`);
        }
      }
      const [month, year] = form.expiry.split('/').map((part) => part.trim());
      const result = await apiService.addCardDirect({
        cardNumber: form.number,
        expiryMonth: month,
        expiryYear: year,
        cvv: form.cvv,
        cardholderName: form.name,
        billingZip: form.zip,
      });
      if (!result?.success) {
        const failure = new Error(result?.error || 'Failed to link your card. Please try again.');
        failure.response = { status: 400, data: result };
        throw failure;
      }
      let points = null;
      if (!alreadyHadCard) {
        const reward = result?.setup_reward;
        if (reward?.awarded && reward.points > 0) {
          points = reward.points;
        } else {
          try {
            const claimed = await apiService.claimSetupTaskReward('link_card');
            if (claimed?.awarded && claimed.points > 0) points = claimed.points;
          } catch (_error) {
            points = null;
          }
        }
      }
      setEarned(points);
      setDone(true);
      onLinked();
    } catch (err) {
      if (err?.response?.data?.error_code === 'CARD_ALREADY_LINKED') {
        setError('This card is already linked to another user');
      } else {
        setError(errorText(err, 'Failed to link your card. Please try again.'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-modal" role="presentation" onClick={saving ? undefined : onClose}>
      <div className="settings-dialog is-wide" role="dialog" aria-modal="true" aria-label="Add card" onClick={(event) => event.stopPropagation()}>
        {done ? (
          <div className="settings-linked">
            <h3>Card linked!</h3>
            <p>
              {earned
                ? `You earned ${Number(earned).toLocaleString('en-US')} pts for linking your card.`
                : 'Your card is linked. Shop at participating stores to earn points.'}
            </p>
            <div className="settings-dialog-actions">
              <button type="button" className="settings-btn is-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h3>Add card</h3>
            <p>Earn points for every purchase and unlock exclusive team perks</p>
            <Field label="Cardholder name">
              <input
                className="settings-input"
                value={form.name}
                onChange={(event) => update('name', event.target.value)}
                placeholder="John Doe"
                autoComplete="cc-name"
              />
            </Field>
            <Field label="Card number">
              <input
                className="settings-input"
                value={form.number}
                onChange={(event) => update('number', formatCardNumber(event.target.value))}
                placeholder="Card number"
                inputMode="numeric"
                autoComplete="cc-number"
                maxLength={19}
              />
            </Field>
            <div className="settings-grid">
              <Field label="Expiry date">
                <input
                  className="settings-input"
                  value={form.expiry}
                  onChange={(event) => update('expiry', formatExpiry(event.target.value))}
                  placeholder="MM / YY"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  maxLength={7}
                />
              </Field>
              <Field label="CVV">
                <input
                  className="settings-input"
                  type="password"
                  value={form.cvv}
                  onChange={(event) => update('cvv', event.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="123"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  maxLength={4}
                />
              </Field>
            </div>
            <Field label="Zip code">
              <input
                className="settings-input"
                value={form.zip}
                onChange={(event) => update('zip', formatZip(event.target.value))}
                placeholder="12345"
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={10}
              />
            </Field>
            <p className="settings-legal">
              By clicking Link card you authorize the payment card network to monitor your payment
              card and share data about all your purchases as required to participate in the
              Program per the <a href="/terms-and-conditions/" target="_blank" rel="noreferrer">Program Terms</a> and{' '}
              <a href="/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a>. Your
              purchase data (date/time, purchase amount, merchant category) will be shared with the
              Program provider Olive, and with HomeCrowd in order to enable card linked offers and to
              provide notifications about reward status, additional data for qualifying transactions
              (merchant name and location) will be shared with Olive, with HomeCrowd, and with
              merchant partners funding the rewards. Data will be accessible until such a time when
              you revoke authorization via Program settings.
            </p>
            {error ? <Notice tone="error">{error}</Notice> : null}
            <div className="settings-dialog-actions">
              <button type="button" className="settings-btn" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="settings-btn is-primary" disabled={saving}>
                {saving ? 'Linking...' : 'Link card'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function LinkedCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await apiService.getPaymentCards();
      const list = response?.results || response?.data || response;
      setCards(Array.isArray(list) ? list.filter((card) => card.status === 'active') : []);
    } catch (_error) {
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const closeManage = () => {
    if (deactivating) return;
    setSelected(null);
    setConfirming(false);
    setError('');
  };

  const deactivate = async () => {
    if (!selected) return;
    setDeactivating(true);
    setError('');
    try {
      const response = await apiService.deactivateCard(selected.olive_card_id || selected.id);
      if (!response?.success) throw new Error(response?.details || 'Failed to deactivate card');
      setCards((prev) => prev.filter((card) => card.id !== selected.id));
      setDeactivating(false);
      setSelected(null);
      setConfirming(false);
    } catch (err) {
      setError(errorText(err, 'Failed to deactivate card. Please try again.'));
      setDeactivating(false);
    }
  };

  const last4 = selected?.last4 || selected?.last_four || '';

  return (
    <div className="settings-section">
      <SectionHead
        title="Linked cards"
        subtitle="Pay with a linked card to earn points."
        action={(
          <button type="button" className="settings-btn is-primary" onClick={() => setAdding(true)}>
            Add card
          </button>
        )}
      />
      <div className="settings-secure">
        {/* The file has no viewBox, so one is set here or the 200px drawing is clipped at 24px. */}
        <LockIcon viewBox="0 0 200 200" aria-hidden="true" />
        <span>
          <strong>Your data is secure</strong>
          <span>We use bank-level encryption and never store your full card details.</span>
        </span>
      </div>
      {loading ? <p className="settings-empty">Loading cards...</p> : null}
      {!loading && !cards.length ? <p className="settings-empty">No linked cards yet</p> : null}
      <div className="settings-cards">
        {cards.map((card, index) => {
          const logo = cardLogo(cardBrand(card));
          return (
            <button key={card.id || index} type="button" className="settings-card-row" onClick={() => setSelected(card)}>
              <span className="settings-card-logo">
                {logo ? <img src={logo} alt="" /> : null}
              </span>
              <strong>{cardLabel(card)}</strong>
              <span className="settings-card-manage">Manage</span>
              <svg viewBox="0 0 8 14" aria-hidden="true">
                <path d="M1.5 1.5 6.5 7l-5 5.5" />
              </svg>
            </button>
          );
        })}
      </div>
      {selected ? (
        <div className="settings-modal" role="presentation" onClick={closeManage}>
          <div className="settings-dialog" role="dialog" aria-modal="true" aria-label="Card options" onClick={(event) => event.stopPropagation()}>
            <h3>{confirming ? 'Deactivate card' : 'Card options'}</h3>
            <p>
              {confirming
                ? `Are you sure you want to deactivate this card ending in ${last4}? This action cannot be undone.`
                : `Card ending in ${last4}`}
            </p>
            {error ? <Notice tone="error">{error}</Notice> : null}
            <div className="settings-dialog-actions">
              <button type="button" className="settings-btn" onClick={closeManage} disabled={deactivating}>
                Cancel
              </button>
              <button
                type="button"
                className="settings-btn is-danger"
                onClick={confirming ? deactivate : () => setConfirming(true)}
                disabled={deactivating}
              >
                {deactivating ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {adding ? <AddCardDialog onClose={() => setAdding(false)} onLinked={load} /> : null}
    </div>
  );
}

function isHomecrowdActivity(entry) {
  const metadata = entry?.metadata || {};
  return (
    ['setup_task_reward', 'incentive_campaign', 'homecrowd_bonus', 'card_draw'].includes(entry?.activity_kind) ||
    metadata.source === 'setup_task_reward' ||
    metadata.source === 'incentive_campaign' ||
    metadata.incentive_type === 'campaign_bonus'
  );
}

function activityTitle(entry) {
  if (entry.display_title) return entry.display_title;
  if (entry.redemption?.reward_title) return entry.redemption.reward_title;
  if (entry.redemption?.reward?.title) return entry.redemption.reward.title;
  if (entry.description) return entry.description;
  if (entry.merchant?.name) return entry.merchant.name;
  if (entry.merchant_name) return entry.merchant_name;
  if (entry.transaction_type === 'earned') {
    if (entry.earning_source === 'bonus') return 'Bonus Points';
    if (entry.earning_source === 'referral') return 'Referral Bonus';
    if (entry.earning_source === 'signup') return 'Signup Bonus';
    return 'Points Earned';
  }
  if (entry.transaction_type === 'spent') {
    return entry.spending_source === 'redemption' ? 'Reward Redemption' : 'Points Spent';
  }
  if (entry.transaction_type === 'adjustment') return 'Manual Adjustment';
  if (entry.transaction_type === 'expired') return 'Points Expired';
  if (entry.transaction_type === 'refund') return 'Points Refunded';
  return 'Transaction';
}

function activityChannel(entry) {
  const source = entry.source || (entry.metadata?.source === 'access_travel' ? 'travel' : '');
  if (source === 'travel') return 'Travel';
  if (source === 'wildfire') return 'Online';
  if (source === 'olive') return 'In store';
  return 'HomeCrowd';
}

function activityDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.floor(Math.abs(Date.now() - date.getTime()) / 86400000);
  if (days === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function ActivityLog({ user }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const school = user?.active_school || user?.activeSchool;
  const schoolLogo = school?.image || '';

  useEffect(() => {
    let cancelled = false;
    apiService
      .getUserActivityLog(50)
      .then((list) => {
        if (!cancelled) setEntries(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const term = search.trim().toLowerCase();
  const filtered = term
    ? entries.filter((entry) =>
        [activityTitle(entry), entry.transaction_type, entry.earning_source, entry.spending_source]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      )
    : entries;

  return (
    <div className="settings-section">
      <SectionHead title="Activity log" subtitle="Every point you earned and spent." />
      <input
        className="settings-input settings-search"
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search activity"
      />
      {loading ? <p className="settings-empty">Loading your activity...</p> : null}
      {!loading && !filtered.length ? (
        <div className="settings-empty">
          <strong>{entries.length ? 'No activity matches your search' : 'No activity yet'}</strong>
          <span>
            {entries.length
              ? 'Try adjusting your search'
              : 'Start earning and spending points to see your activity here!'}
          </span>
        </div>
      ) : null}
      <ul className="settings-activity">
        {filtered.map((entry) => {
          const points = Number(entry.points) || 0;
          const source = entry.source || (entry.metadata?.source === 'access_travel' ? 'travel' : '');
          const mark = {
            logoUrl: !isHomecrowdActivity(entry) ? entry.merchant_logo_url || entry.logo_url || '' : '',
            source: source === 'travel' || source === 'wildfire' || source === 'olive' ? source : '',
          };
          const amount = entry.amount != null && entry.amount !== '' ? Number(entry.amount) : null;
          return (
            <li key={entry.id}>
              <ActivityAvatar entry={mark} schoolLogo={schoolLogo} />
              <span className="settings-activity-copy">
                <strong>{activityTitle(entry)}</strong>
                <span>{[activityChannel(entry), activityDate(entry.date)].filter(Boolean).join(' · ')}</span>
              </span>
              <span className="settings-activity-points">
                <strong className={points < 0 ? 'is-negative' : ''}>
                  {points > 0 ? `+${points.toLocaleString('en-US')}` : points.toLocaleString('en-US')} pts
                </strong>
                {Number.isFinite(amount) ? <span>${amount.toFixed(2)}</span> : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SettingsPage() {
  const { user } = useShellUser();
  const [params, setParams] = useSearchParams();
  // Help & support is a tab only once support has written: an open thread with messages, or a
  // closed one that still has something unread.
  const [hasSupport, setHasSupport] = useState(false);
  useEffect(() => {
    let cancelled = false;
    apiService
      .getMySupportThread()
      .then((thread) => {
        if (cancelled || !thread) return;
        const messages = Array.isArray(thread.messages) ? thread.messages : [];
        const unread = Number(thread.unread_count) > 0;
        setHasSupport(thread.exists !== false && messages.length > 0 && (!thread.closed || unread));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const tabs = TABS.filter((tab) => {
    if (tab.id === 'activity') return !isEarlyRelease(user);
    if (tab.id === 'support') return hasSupport;
    return true;
  });
  const requested = params.get('tab');
  const active = tabs.some((tab) => tab.id === requested) ? requested : 'personal';

  return (
    <div className="settings-page">
      <nav className="settings-tabs" role="tablist" aria-label="Settings">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active === tab.id}
            className={active === tab.id ? 'is-on' : ''}
            onClick={() => setParams(tab.id === 'personal' ? {} : { tab: tab.id })}
          >
            {tab.label}
          </button>
        ))}
      </nav>
      {active === 'personal' ? <PersonalInfo /> : null}
      {active === 'security' ? <Security /> : null}
      {active === 'cards' ? <LinkedCards /> : null}
      {active === 'activity' ? <ActivityLog user={user} /> : null}
      {active === 'support' ? <SupportInbox /> : null}
    </div>
  );
}

function SettingsGate() {
  if (!apiService.isAuthenticated()) return <Navigate to="/login" replace />;
  return (
    <AppShell title="Settings">
      <SettingsPage />
    </AppShell>
  );
}

export default SettingsGate;
